import React, {Dispatch, SetStateAction} from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useContext,
  useEffect,
  useResponsive,
  useMemo,
  useState,
  ResponsiveType,
  useToast,
} from '@supermove/hooks';
import {
  LeadProviderEndpoint,
  LeadProviderGlobalIntegration,
  ProjectTypeModel,
  TagModel,
  UserModel,
} from '@supermove/models';
import {Typography, colors} from '@supermove/styles';

// App
import Button from '@shared/design/components/Button';
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import DropdownInput from '@shared/design/components/DropdownInput';
import FieldInput from '@shared/design/components/Field/FieldInput';
import FieldValue from '@shared/design/components/Field/FieldValue';
import MultiDropdownCheckboxInput from '@shared/design/components/Field/MultiDropdownCheckboxInput';
import ConfirmationModal from '@shared/design/components/Modal/SmallModal/ConfirmationModal';
import Panel from '@shared/design/components/Panel';
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import ProgressModal from '@shared/design/components/ProgressModal';
import RadioButton from '@shared/design/components/RadioButton';
import SearchBar from '@shared/design/components/SearchBar';
import Table from '@shared/design/components/Table';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import useProgress from '@shared/modules/App/hooks/useProgress';
import TagPill from '@shared/modules/Tag/components/TagPill';
import TagKind from '@shared/modules/Tag/enums/TagKind';
import UserRole from '@shared/modules/User/enums/UserRole';
import useCreateLeadProviderEndpointMutation, {
  CreateLeadProviderEndpointResponse,
} from 'modules/Organization/Settings/Company/components/LeadProvider/useCreateLeadProviderEndpointMutation';

enum StepKind {
  LeadProvider = 'LeadProvider',
  AdditionalInformation = 'AdditionalInformation',
  Review = 'Review',
}

interface Step {
  kind: StepKind;
  title: string;
  description: string;
}

class Steps {
  static steps = [
    {
      kind: StepKind.LeadProvider,
      title: 'Lead Provider',
      description: 'Create a custom lead provider or choose from the supported options below.',
    },
    {
      kind: StepKind.AdditionalInformation,
      title: 'Additional Information',
      description: 'Enter the following details to map a lead to a Supermove project.',
    },
    {
      kind: StepKind.Review,
      title: 'Review',
      description: 'Before submitting, make sure this information is correct.',
    },
  ];

  static getPreviousStepKind(kind: string): StepKind | null {
    switch (kind) {
      case StepKind.LeadProvider:
        return null;
      case StepKind.AdditionalInformation:
        return StepKind.LeadProvider;
      case StepKind.Review:
        return StepKind.AdditionalInformation;
      default:
        throw Error(`Unknown step kind ${kind}`);
    }
  }

  static getNextStepKind(kind: string): StepKind | null {
    switch (kind) {
      case StepKind.LeadProvider:
        return StepKind.AdditionalInformation;
      case StepKind.AdditionalInformation:
        return StepKind.Review;
      case StepKind.Review:
        return null;
      default:
        throw Error(`Unknown step kind ${kind}`);
    }
  }

  static getStep(kind: string): Step {
    const step = Steps.steps.find((step) => step.kind === kind);
    if (!step) {
      throw Error(`Unknown step kind ${kind}`);
    }
    return step;
  }
}

interface LeadProviderOption {
  id: string | null;
  name: string;
  description: string;
  descriptionUrl: string;
  requiresProjectType: boolean;
}

const MobilePaddingContainer = Styled.View<{responsive: ResponsiveType}>`
  padding-horizontal: ${({responsive}) => (responsive.desktop ? 0 : 16)}px;
`;

const StepHeaderText = Styled.Text`
  ${Typography.Responsive.Heading2}
`;

const StepHeaderDescription = Styled.Text`
  ${Typography.Responsive.Body}
  color: ${colors.gray.secondary};
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const CardName = Styled.Text`
  ${Typography.Responsive.Label}
  text-align: left;
`;

const CardDescription = Styled.Text`
  ${Typography.Responsive.Micro}
  color: ${colors.gray.secondary};
  text-align: left;
`;

const CardLink = Styled.Text`
  ${Typography.Responsive.Link}
  text-align: center;
`;

const EditButton = Styled.ButtonV2`
  flex-direction: row;
  align-items: center;
`;

const EditButtonText = Styled.Text`
  ${Typography.Responsive.Label}
  color: ${colors.blue.interactive};
  margin-left: 4px;
`;

const FormRoot = 'createLeadProviderEndpointForm';

type Form = ReturnType<typeof useCreateLeadProviderEndpointMutation>['form'];

const ResponsiveContext = React.createContext<ResponsiveType>({
  desktop: false,
  tablet: false,
  mobile: false,
});

export const CreateLeadProviderEndpointModal = ({
  isOpen,
  handleClose,
  refetch,
  data,
}: {
  isOpen: boolean;
  handleClose: () => void;
  refetch: () => void;
  data: CreateLeadProviderEndpointModalData;
}) => {
  const {
    viewer: {
      role,
      viewingOrganization: {
        id: organizationId,
        features,
        leadProviderEndpoints,
        projectTypes,
        projectTags,
        jobTags,
      },
    },
    leadProviderGlobalIntegrations,
  } = data;
  const leadProviderOptions: LeadProviderOption[] = [
    ...leadProviderGlobalIntegrations.map(
      ({id, name, description, descriptionUrl, requiresProjectType}) => {
        return {
          id,
          name,
          description,
          descriptionUrl,
          requiresProjectType,
        };
      },
    ),
    ...(role === UserRole.SUPER_ADMIN || features.isEnabledCustomLeadProviderOptions
      ? [
          {
            id: null,
            name: 'Custom Website Integration',
            description:
              'Requires professional services hours to implement. Used for custom website setups that requires additional data mapping and support.',
            descriptionUrl: '',
            requiresProjectType: false,
          },
          {
            id: null,
            name: 'Custom Lead Provider',
            description:
              'Requires professional services hours to implement. If your provider is not included in the list above, select this option to request a new provider to be supported.',
            descriptionUrl: '',
            requiresProjectType: false,
          },
        ]
      : []),
  ];

  const [successResponse, setSuccessResponse] = useState<CreateLeadProviderEndpointResponse | null>(
    null,
  );
  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: 'Something went wrong. Please refresh the page and try again.',
  });
  const {form, submitting, handleSubmit} = useCreateLeadProviderEndpointMutation({
    createLeadProviderEndpointForm: {
      organizationId,
      name: '',
      description: '',
      globalFields: undefined,
      projectTagIds: [],
      jobTagIds: [],
    },
    validate: ({createLeadProviderEndpointForm}) => {
      // Handle validations client-side, since form fields are spread out across multiple steps in
      // the modal.
      const errors: Record<string, string> = {};
      const isDuplicateName = leadProviderEndpoints
        .map((endpoint) => endpoint.inboundWebhookEndpoint.name.toLowerCase())
        .includes((createLeadProviderEndpointForm.name || '').toLowerCase().trim());
      if (createLeadProviderEndpointForm.name && isDuplicateName) {
        errors[`${FormRoot}.name`] =
          'A lead provider with this name already exists. Please use a different name.';
      }
      return errors;
    },
    onSuccess: (response: CreateLeadProviderEndpointResponse) => {
      setSuccessResponse(response);
      refetch();
    },
    onError: (errors) => {
      console.error(errors);
      errorToast.handleToast();
    },
  });

  const {
    currentStepKind,
    setCurrentStepKind,
    isViewingFirstStep,
    completedSteps,
    setCompletedSteps,
    nextStepToComplete,
    currentStepIndex,
  } = useProgress({
    steps: Steps.steps,
    getPreviousStepKind: Steps.getPreviousStepKind,
    getNextStepKind: Steps.getNextStepKind,
  });
  const currentStep = useMemo(() => Steps.getStep(currentStepKind), [currentStepKind]);
  const setCurrentStep = React.useCallback(
    (kind: StepKind) => {
      setCurrentStepKind(kind);
      const completedSteps = Steps.steps.slice(
        0,
        Steps.steps.findIndex((step) => kind === step.kind),
      );
      setCompletedSteps(completedSteps.map((step) => step.kind));
    },
    [setCompletedSteps],
  );

  const [selectedLeadProvider, setSelectedLeadProvider] = useState<LeadProviderOption | undefined>(
    undefined,
  );
  const [isActionEnabled, setIsActionEnabled] = useState(false);

  const responsive = useResponsive();

  if (successResponse) {
    return (
      <CreateLeadProviderEndpointSuccessModal
        response={successResponse}
        isOpen={isOpen}
        handleClose={handleClose}
      />
    );
  }

  return (
    <ResponsiveContext.Provider value={responsive}>
      <ProgressModal
        isOpen={isOpen}
        handleClose={handleClose}
        steps={Steps.steps}
        completedSteps={completedSteps}
        isViewingFirstStep={isViewingFirstStep}
        currentStepKind={currentStepKind}
        getStepTitle={(kind) => Steps.getStep(kind).title}
        setCurrentStepKind={setCurrentStepKind}
        nextStepToComplete={nextStepToComplete}
        currentStepIndex={currentStepIndex}
        title={'Add Lead Provider'}
        handleBack={() => setCurrentStep((completedSteps.at(-1) as StepKind) || StepKind.Review)}
        BodyElement={
          <ModalBody
            form={form}
            leadProviderOptions={leadProviderOptions}
            projectTypes={projectTypes}
            projectTags={projectTags}
            jobTags={jobTags}
            selectedLeadProvider={selectedLeadProvider}
            setSelectedLeadProvider={setSelectedLeadProvider}
            setIsActionEnabled={setIsActionEnabled}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        }
        FooterElement={
          <ProgressFooter
            currentStep={currentStep}
            handleBack={() => {
              const previousStep = Steps.getPreviousStepKind(currentStep.kind);
              if (!previousStep) {
                return;
              }
              setCurrentStep(previousStep);
            }}
            handleNext={() => {
              const nextStep = Steps.getNextStepKind(currentStep.kind);
              if (!nextStep) {
                return;
              }
              setCurrentStep(nextStep);
            }}
            handleSave={handleSubmit}
            isActionEnabled={isActionEnabled}
            isSubmitting={submitting}
          />
        }
      />
    </ResponsiveContext.Provider>
  );
};

const ModalBody = ({
  form,
  leadProviderOptions,
  projectTypes,
  projectTags,
  jobTags,
  selectedLeadProvider,
  setSelectedLeadProvider,
  setIsActionEnabled,
  currentStep,
  setCurrentStep,
}: {
  form: Form;
  leadProviderOptions: LeadProviderOption[];
  projectTypes: ProjectTypeModel[];
  projectTags: TagModel[];
  jobTags: TagModel[];
  selectedLeadProvider?: LeadProviderOption;
  setSelectedLeadProvider: Dispatch<SetStateAction<LeadProviderOption | undefined>>;
  setIsActionEnabled: Dispatch<SetStateAction<boolean>>;
  currentStep: Step;
  setCurrentStep: (kind: StepKind) => void;
}) => {
  const responsive = useContext(ResponsiveContext);

  return (
    <React.Fragment>
      <MobilePaddingContainer responsive={responsive}>
        <StepHeaderText responsive={responsive}>{currentStep.title}</StepHeaderText>
        <Space height={8} />
        <StepHeaderDescription responsive={responsive}>
          {currentStep.description}
        </StepHeaderDescription>
      </MobilePaddingContainer>
      <Space height={16} />
      <MobilePaddingContainer responsive={responsive}>
        {(() => {
          switch (currentStep.kind) {
            case StepKind.LeadProvider:
              setIsActionEnabled(!!selectedLeadProvider);
              return (
                <StepLeadProvider
                  form={form}
                  leadProviderOptions={leadProviderOptions}
                  selectedLeadProvider={selectedLeadProvider}
                  setSelectedLeadProvider={setSelectedLeadProvider}
                  setCurrentStep={setCurrentStep}
                />
              );

            case StepKind.AdditionalInformation:
              setIsActionEnabled(
                form.isValid &&
                  !!form.values.createLeadProviderEndpointForm.name &&
                  !!form.values.createLeadProviderEndpointForm.description &&
                  (!selectedLeadProvider!.requiresProjectType ||
                    (!!form.values.createLeadProviderEndpointForm.globalFields?.projectTypeId &&
                      !!form.values.createLeadProviderEndpointForm.globalFields?.jobTypeId)),
              );
              return (
                <React.Fragment>
                  <StepAdditionalInformationNameDescription form={form} />
                  {selectedLeadProvider!.requiresProjectType && (
                    <React.Fragment>
                      <StepAdditionalInformationProjectType
                        form={form}
                        projectTypes={projectTypes}
                      />
                      <StepAdditionalInformationTags
                        form={form}
                        projectTags={projectTags}
                        jobTags={jobTags}
                      />
                    </React.Fragment>
                  )}
                </React.Fragment>
              );

            case StepKind.Review:
              setIsActionEnabled(true);
              return (
                <StepReview
                  form={form}
                  selectedLeadProvider={selectedLeadProvider!}
                  projectTypes={projectTypes}
                  projectTags={projectTags}
                  jobTags={jobTags}
                  setCurrentStep={setCurrentStep}
                />
              );
          }
        })()}
      </MobilePaddingContainer>
      {responsive.desktop && <Space height={24} />}
    </React.Fragment>
  );
};

const StepLeadProvider = ({
  form,
  leadProviderOptions,
  selectedLeadProvider,
  setSelectedLeadProvider,
  setCurrentStep,
}: {
  form: Form;
  leadProviderOptions: LeadProviderOption[];
  selectedLeadProvider?: LeadProviderOption;
  setSelectedLeadProvider: Dispatch<SetStateAction<LeadProviderOption | undefined>>;
  setCurrentStep: (kind: StepKind) => void;
}) => {
  const [search, setSearch] = useState('');

  const columnDefinitions = [
    {
      width: 30,
      cellComponent: (leadProviderOption: LeadProviderOption) => {
        return (
          <Row>
            <RadioButton
              isOn={leadProviderOption.name === selectedLeadProvider?.name}
              onPress={(isOn) => {
                // Clear all state if a different lead provider option is selected
                setSelectedLeadProvider(leadProviderOption);
                setCurrentStep(StepKind.LeadProvider);
                form.resetForm();
                form.setErrors({});
                form.setFieldValue(`${FormRoot}.globalFields`, null);
                form.setFieldValue(`${FormRoot}.projectTagIds`, []);
                form.setFieldValue(`${FormRoot}.jobTagIds`, []);
                if (leadProviderOption.id) {
                  form.setFieldValue(
                    `${FormRoot}.globalFields.leadProviderGlobalIntegrationId`,
                    leadProviderOption.id,
                  );
                }
                if (leadProviderOption.requiresProjectType) {
                  form.setFieldValue(`${FormRoot}.name`, leadProviderOption.name);
                  form.setFieldValue(`${FormRoot}.description`, leadProviderOption.description);
                }
              }}
            />
          </Row>
        );
      },
      cellStyle: {
        alignSelf: 'flex-start',
        marginTop: 1,
      },
    },
    {
      headerLabel: 'Lead Provider',
      flex: 4,
      cellComponent: (leadProviderOption: LeadProviderOption) => (
        <CardName>{leadProviderOption.name}</CardName>
      ),
      secondary: {
        cellComponent: (leadProviderOption: LeadProviderOption) => (
          <CardDescription>{leadProviderOption.description}</CardDescription>
        ),
      },
    },
    {
      headerLabel: 'Guide',
      flex: 1,
      cellComponent: (leadProviderOption: LeadProviderOption) =>
        leadProviderOption.descriptionUrl ? (
          <a href={leadProviderOption.descriptionUrl} target={'_blank'}>
            <CardLink>{'Link'}</CardLink>
            <Icon
              source={Icon.ArrowUpRightFromSquare}
              size={12}
              color={colors.blue.interactive}
              style={{marginTop: 4, marginLeft: 4}}
            />
          </a>
        ) : (
          <React.Fragment />
        ),
    },
  ];

  return (
    <React.Fragment>
      <SearchBar
        containerStyle={{flex: 1, marginBottom: 16}}
        onChangeText={setSearch}
        placeholder={'Search lead providers'}
        style={{width: '100%'}}
        defaultValue={search}
      />
      <Table
        items={leadProviderOptions.filter(
          (leadProviderOption) =>
            leadProviderOption.name.toLowerCase().includes(search.trim().toLowerCase()) ||
            leadProviderOption.name === selectedLeadProvider?.name,
        )}
        itemKey={'name'}
        columnDefinitions={columnDefinitions}
        hasBorder
        emptyStateText={'No lead providers found. Try searching something different.'}
        isMobileTable
      />
    </React.Fragment>
  );
};

const StepAdditionalInformationProjectType = ({
  form,
  projectTypes,
}: {
  form: Form;
  projectTypes: ProjectTypeModel[];
}) => {
  const projectType = projectTypes.find(
    (projectType) =>
      projectType.id === form.values.createLeadProviderEndpointForm.globalFields?.projectTypeId,
  );

  return (
    <React.Fragment>
      <Space height={16} />
      <FieldInput
        {...form}
        component={DropdownInput}
        name={`${FormRoot}.globalFields.projectTypeId`}
        label={'Project Type'}
        input={{
          placeholder: 'Select project type',
          options: projectTypes.map((projectType) => {
            return {
              label: projectType.name,
              value: projectType.id,
            };
          }),
          setFieldValue: (label: string, projectTypeId: string) => {
            form.setFieldValue(`${FormRoot}.globalFields.jobTypeId`, null);
            form.setFieldValue(`${FormRoot}.globalFields.projectTypeId`, projectTypeId);
          },
          style: {width: '100%'},
          isPortaled: true,
        }}
        isResponsive
        isRequired
      />
      <Space height={16} />
      <FieldInput
        {...form}
        component={DropdownInput}
        name={`${FormRoot}.globalFields.jobTypeId`}
        label={'Job Type'}
        input={{
          placeholder: 'Select job type',
          options:
            projectType?.jobTypes.map((jobType) => {
              return {
                label: jobType.name,
                value: jobType.id,
              };
            }) || [],
          setFieldValue: (label: string, jobTypeId: string) => {
            form.setFieldValue(`${FormRoot}.globalFields.jobTypeId`, jobTypeId);
          },
          isDisabled: !projectType,
          style: {width: '100%'},
          isPortaled: true,
        }}
        isResponsive
        isRequired
      />
    </React.Fragment>
  );
};

const StepAdditionalInformationNameDescription = ({form}: {form: Form}) => {
  useEffect(() => {
    // Trigger validation on initial render - a lead provider with this name may already exist
    form.setFieldTouched(`${FormRoot}.name`, true, true);
  }, []);

  return (
    <React.Fragment>
      <FieldInput
        {...form}
        name={`${FormRoot}.name`}
        label={'Name'}
        isResponsive
        isRequired
        input={{
          placeholder: 'Enter name',
        }}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        name={`${FormRoot}.description`}
        label={'Description'}
        isResponsive
        isRequired
        input={{
          placeholder: 'Enter description or URL',
        }}
        tooltip={'Enter the URL of your custom website or lead provider.'}
      />
    </React.Fragment>
  );
};

const StepAdditionalInformationTags = ({
  form,
  projectTags,
  jobTags,
}: {
  form: Form;
  projectTags: TagModel[];
  jobTags: TagModel[];
}) => {
  const projectTagOptions = projectTags
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((tag) => tag.kind !== TagKind.TRAINING_PROJECT)
    .map((tag) => ({
      value: tag.id,
      label: `${tag.emoji} ${tag.name}`,
    }));
  const jobTagOptions = jobTags
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((tag) => ({
      value: tag.id,
      label: `${tag.emoji} ${tag.name}`,
    }));
  return (
    <React.Fragment>
      <Space height={16} />
      <FieldInput
        {...form}
        name={`${FormRoot}.projectTagIds`}
        component={MultiDropdownCheckboxInput}
        label={'Project Tags'}
        isResponsive
        input={{
          isPortaled: true,
          isSearchable: true,
          options: projectTagOptions,
          placeholder: 'Select project tags',
          setFieldValue: form.setFieldValue,
          style: {width: '100%'},
          usePills: true,
          isResponsive: true,
        }}
        style={{flex: 1}}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        name={`${FormRoot}.jobTagIds`}
        component={MultiDropdownCheckboxInput}
        label={'Job Tags'}
        isResponsive
        input={{
          isPortaled: true,
          isSearchable: true,
          options: jobTagOptions,
          placeholder: 'Select job tags',
          setFieldValue: form.setFieldValue,
          style: {width: '100%'},
          usePills: true,
          isResponsive: true,
        }}
        style={{flex: 1}}
      />
    </React.Fragment>
  );
};

const StepReview = ({
  form,
  selectedLeadProvider,
  projectTypes,
  projectTags,
  jobTags,
  setCurrentStep,
}: {
  form: Form;
  projectTypes: ProjectTypeModel[];
  projectTags: TagModel[];
  jobTags: TagModel[];
  selectedLeadProvider: LeadProviderOption;
  setCurrentStep: (kind: StepKind) => void;
}) => {
  return (
    <React.Fragment>
      <ActionPanel
        HeaderComponent={() => (
          <Panel.Header>
            <Panel.HeaderText style={{flex: 1}}>{'Lead Provider'}</Panel.HeaderText>
            <EditButton
              onPress={() => {
                setCurrentStep(StepKind.LeadProvider);
              }}
            >
              <Icon source={Icon.Pen} size={12} color={colors.blue.interactive} />
              <EditButtonText>{'Edit'}</EditButtonText>
            </EditButton>
          </Panel.Header>
        )}
        BodyComponent={() => {
          return (
            <React.Fragment>
              <FieldValue label={'Lead Provider'} value={selectedLeadProvider.name} isResponsive />
            </React.Fragment>
          );
        }}
      />
      <Space height={16} />
      <ActionPanel
        HeaderComponent={() => (
          <Panel.Header>
            <Panel.HeaderText style={{flex: 1}}>{'Additional Information'}</Panel.HeaderText>
            <EditButton
              onPress={() => {
                setCurrentStep(StepKind.AdditionalInformation);
              }}
            >
              <Icon source={Icon.Pen} size={12} color={colors.blue.interactive} />
              <EditButtonText>{'Edit'}</EditButtonText>
            </EditButton>
          </Panel.Header>
        )}
        BodyComponent={() => {
          const projectType = projectTypes.find(
            (projectType) =>
              projectType.id ===
              form.values.createLeadProviderEndpointForm.globalFields?.projectTypeId,
          );
          const jobType = projectType?.jobTypes.find(
            (jobType) =>
              jobType.id === form.values.createLeadProviderEndpointForm.globalFields?.jobTypeId,
          );
          const assignedProjectTags = projectTags.filter((tag) =>
            form.values.createLeadProviderEndpointForm.projectTagIds.includes(`${tag.id}`),
          );
          const assignedJobTags = jobTags.filter((tag) =>
            form.values.createLeadProviderEndpointForm.jobTagIds.includes(`${tag.id}`),
          );
          return (
            <React.Fragment>
              <FieldValue
                label={'Name'}
                value={form.values.createLeadProviderEndpointForm.name}
                isResponsive
              />
              <Space height={16} />
              <FieldValue
                label={'Description'}
                value={form.values.createLeadProviderEndpointForm.description}
                isResponsive
              />
              {selectedLeadProvider.requiresProjectType && (
                <React.Fragment>
                  <Space height={16} />
                  <FieldValue label={'Project Type'} value={projectType!.name} isResponsive />
                  <Space height={16} />
                  <FieldValue label={'Job Type'} value={jobType!.name} isResponsive />
                  <Space height={16} />
                  <FieldValue label={'Project Tags'} isResponsive />
                  {assignedProjectTags.length === 0 ? (
                    <FieldValue.EmptyText>{'None'}</FieldValue.EmptyText>
                  ) : (
                    <Row style={{flexWrap: 'wrap'}}>
                      {assignedProjectTags.map((tag) => (
                        <React.Fragment key={tag.id}>
                          <TagPill tag={tag} hasDeleteButton={false} isSmall />
                          <Space width={8} />
                        </React.Fragment>
                      ))}
                    </Row>
                  )}
                  <Space height={16} />
                  <FieldValue label={'Job Tags'} isResponsive />
                  {assignedJobTags.length === 0 ? (
                    <FieldValue.EmptyText>{'None'}</FieldValue.EmptyText>
                  ) : (
                    <Row style={{flexWrap: 'wrap'}}>
                      {assignedJobTags.map((tag) => (
                        <React.Fragment key={tag.id}>
                          <TagPill tag={tag} hasDeleteButton={false} isSmall />
                          <Space width={8} />
                        </React.Fragment>
                      ))}
                    </Row>
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          );
        }}
      />
    </React.Fragment>
  );
};

const ProgressFooter = ({
  currentStep,
  handleBack,
  handleNext,
  handleSave,
  isActionEnabled,
  isSubmitting,
}: {
  currentStep: Step;
  handleBack: () => void;
  handleNext: () => void;
  handleSave: () => void;
  isActionEnabled: boolean;
  isSubmitting: boolean;
}) => {
  const responsive = useResponsive();
  return (
    <React.Fragment>
      {Steps.getPreviousStepKind(currentStep.kind) && responsive.desktop && (
        <React.Fragment>
          <SecondaryButton
            text={'Back'}
            onPress={handleBack}
            iconLeft={Icon.ArrowLeft}
            iconSize={12}
          />
          <Space style={{flex: 1}} />
        </React.Fragment>
      )}
      {currentStep.kind === StepKind.Review ? (
        <Button
          text={'Save'}
          onPress={handleSave}
          isDisabled={!isActionEnabled}
          isSubmitting={isSubmitting}
          isWidthOfContainer={!responsive.desktop}
          style={{width: '100%'}}
        />
      ) : (
        <Button
          text={'Next'}
          onPress={handleNext}
          isDisabled={!isActionEnabled}
          isWidthOfContainer={!responsive.desktop}
          style={{width: '100%'}}
        />
      )}
    </React.Fragment>
  );
};

CreateLeadProviderEndpointModal.fragment = gql`
  ${TagPill.fragment}
  fragment CreateLeadProviderEndpointModal on Query {
    leadProviderGlobalIntegrations(status: ["active"]) {
      id
      name
      description
      descriptionUrl
      status
      url
      requiresProjectType
    }
    viewer {
      id
      role
      viewingOrganization {
        id
        features {
          isEnabledCustomLeadProviderOptions: isEnabled(feature: "CUSTOM_LEAD_PROVIDER_OPTIONS")
        }
        leadProviderEndpoints {
          id
          leadProviderGlobalIntegration {
            id
          }
          inboundWebhookEndpoint {
            id
            name
          }
        }
        projectTags: tagsByFields(categories: ["PROJECT"], isArchived: false) {
          ...TagPill
          id
          name
          emoji
          kind
        }
        jobTags: tagsByFields(categories: ["JOB"], isArchived: false) {
          ...TagPill
          id
          name
          emoji
          kind
        }
        projectTypes: projectTypesForCategory(category: "MOVE") {
          id
          name
          jobTypes {
            id
            name
          }
        }
      }
    }
  }
`;

export interface CreateLeadProviderEndpointModalData {
  leadProviderGlobalIntegrations: LeadProviderGlobalIntegration[];
  viewer: {
    id: string;
    role: UserModel['role'];
    viewingOrganization: {
      id: string;
      features: {
        isEnabledCustomLeadProviderOptions: boolean;
      };
      leadProviderEndpoints: LeadProviderEndpoint[];
      projectTypes: ProjectTypeModel[];
      projectTags: TagModel[];
      jobTags: TagModel[];
    };
  };
}

export const CreateLeadProviderEndpointSuccessModal = ({
  response,
  isOpen,
  handleClose,
}: {
  response: CreateLeadProviderEndpointResponse;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const isGlobalIntegration = !!response.leadProviderEndpoint.leadProviderGlobalIntegration;
  const copiedToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Webhook URL copied.',
  });
  return (
    <ConfirmationModal
      isOpen={isOpen}
      icon={Icon.ArrowRight}
      title={'Lead Provider Added!'}
      subtitle={'Copy the webhook URL below and share it with your lead provider.'}
      primaryActionText={'Copy Webhook URL'}
      handlePrimaryAction={() => {
        navigator.clipboard.writeText(response.leadProviderEndpoint.inboundWebhookEndpoint.url);
        copiedToast.handleToast();
      }}
      secondaryActionText={isGlobalIntegration ? 'View Help Guide' : undefined}
      handleSecondaryAction={
        isGlobalIntegration
          ? () => {
              const url =
                response.leadProviderEndpoint.leadProviderGlobalIntegration?.descriptionUrl ||
                'https://help.supermove.com/articles/lead-providers-overview-tkEceQ3AWK';
              window.open(url);
            }
          : undefined
      }
      handlePressOutside={handleClose}
    />
  );
};
